// DSOK Dispensing Orders Settings

// Color Configuration
export const ORDER_BOX_COLORS = {
  // Fresh and vibrant color palette for order boxes (4 colors in repeating order)
  colors: [
    "#FF5722", // Fresh Orange
    "#4CAF50", // Vibrant Green
    "#9C27B0", // Vibrant Purple
    "#2196F3", // Fresh Blue
  ],

  // Get color by index (repeating pattern)
  getColorByIndex: (index) => {
    return ORDER_BOX_COLORS.colors[index % ORDER_BOX_COLORS.colors.length];
  },
};

// Display Settings
export const DISPLAY_SETTINGS = {
  // Maximum number of items to show in each order box
  maxItemsPerBox: 2,

  // Show "more items" indicator when there are additional items
  showMoreItemsIndicator: true,

  // Show priority chips (removed)
  showPriority: true,

  // Show estimated time for in-progress orders
  showEstimatedTime: true,

  // Show ready time for completed orders
  showReadyTime: true,

  // Show total items count at bottom of each box
  showTotalItems: true,

  // Show locator number on order boxes
  showLocator: true,

  // Box styling
  boxStyling: {
    minHeight: "250px",
    maxWidth: "250px",
    transition: "transform 0.2s ease-in-out",
    hoverTransform: "translateY(-2px)",
    // Responsive maxWidth for better visual balance
    responsiveMaxWidth: {
      xs: "100%", // Full width on mobile
      sm: "280px", // Slightly larger on small screens
      md: "260px", // Medium screens (3 boxes per row)
      lg: "200px", // Large screens (8 boxes per row)
      xl: "180px", // Extra large screens (12 boxes per row)
    },
  },
};

// Section Configuration
export const SECTION_CONFIG = {
  inProgress: {
    titleColor: "#FFFFFF",
    backgroundColor: "#4CAF50",
    padding: "12px 20px",
    borderRadius: "8px",
    fontWeight: "bold",
  },
  ready: {
    titleColor: "#FFFFFF",
    backgroundColor: "#2196F3",
    padding: "12px 20px",
    borderRadius: "8px",
    fontWeight: "bold",
  },
};

// Grid Configuration
export const GRID_CONFIG = {
  container: {
    spacing: 1,
    // Responsive spacing for better visual balance
    responsiveSpacing: {
      xs: 1, // Tighter spacing on mobile
      sm: 1, // Standard spacing on small screens
      md: 1.5, // Slightly more spacing on medium screens
      lg: 2, // More spacing on large screens
      xl: 2, // More spacing on extra large screens
    },
  },
  item: {
    xs: 12, // 1 box per row on extra small screens (< 600px)
    sm: 6, // 2 boxes per row on small screens (600px - 960px)
    md: 4, // 3 boxes per row on medium screens (960px - 1200px)
    lg: 3, // 8 boxes per row on large screens (1200px - 1920px)
    xl: 2, // 12 boxes per row on extra large screens (>= 1920px)
  },
};

// Styled Components Configuration
export const STYLED_COMPONENTS = {
  // Order Number styling
  orderNumber: {
    fontWeight: "700",
    fontSize: "1rem",
    margin: "0",
    backgroundColor: "white",
    color: "#333",
    padding: "4px 8px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "28px",
    minWidth: "fit-content",
  },

  // Takeaway Chip styling
  takeawayChip: {
    backgroundColor: "transparent",
    color: "#FFD700",
    fontWeight: "700",
    fontSize: "0.8rem",
    height: "28px",
    display: "flex",
    alignItems: "center",
    "& .MuiChip-icon": {
      color: "#FFD700",
      fontSize: "1.3rem",
    },
  },

  // Locator Chip styling
  locatorChip: {
    color: "white",
    fontWeight: "700",
    fontSize: "0.8rem",
    border: "1px solid white",
    backgroundColor: "transparent",
    height: "28px",
    display: "flex",
    alignItems: "center",
  },

  // Order Box Container styling
  orderBoxContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
    minHeight: "32px",
  },

  // Order Info Container styling
  orderInfoContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  // Order Item styling
  orderItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
  },

  // Item Name styling
  itemName: {
    fontWeight: "500",
    fontSize: "0.7rem",
    maxWidth: "70%",
  },

  // Item Quantity styling
  itemQuantity: {
    fontWeight: "600",
  },

  // Order Date styling
  orderDate: {
    fontSize: "0.7rem",
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: "500",
  },

  // Created At styling
  createdAt: {
    fontSize: "0.7rem",
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: "600",
    marginTop: "4px",
  },

  // Divider styling
  divider: {
    backgroundColor: "rgba(255,255,255,0.3)",
    marginBottom: "8px",
  },

  // Time Ago styling
  timeAgo: {
    color: "white",
    fontSize: "0.8rem",
    fontStyle: "italic",
    fontWeight: "bold",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    padding: "2px 6px",
    borderRadius: "4px",
    display: "block",
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    textAlign: "center",
  },

  // Start Time styling
  startTime: {
    fontSize: "0.7rem",
    color: "rgba(255, 255, 255, 0.8)",
    fontWeight: "500",
    marginTop: "4px",
  },

  // Total Items styling
  totalItems: {
    fontSize: "0.8rem",
    color: "rgba(255, 255, 255, 0.9)",
    fontWeight: "600",
    marginTop: "8px",
    textAlign: "center",
  },
};

// Modal Configuration
export const MODAL_CONFIG = {
  // Modal styling
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  // Modal content styling
  modalContent: {
    backgroundColor: "white",
    borderRadius: "8px",
    padding: "32px",
    maxWidth: "700px",
    width: "50%",
    maxHeight: "80vh",
    overflow: "auto",
    position: "relative",
  },

  // Modal title styling
  modalTitle: {
    marginBottom: "16px",
    fontWeight: "bold",
    fontSize: "1.5rem",
  },

  // Modal buttons container styling
  modalButtonsContainer: {
    display: "flex",
    gap: "16px",
    justifyContent: "center",
    marginTop: "24px",
  },

  // Modal button styling
  modalButton: {
    padding: "12px 24px",
    fontSize: "1rem",
    fontWeight: "600",
    minWidth: "200px",
    minHeight: "100px",
  },

  // Cancel button styling
  cancelButton: {
    backgroundColor: "#f44336",
    color: "white",
    "&:hover": {
      backgroundColor: "#d32f2f",
    },
  },

  // In Progress button styling
  inProgressButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    "&:hover": {
      backgroundColor: "#388E3C",
    },
  },

  // Ready button styling
  readyButton: {
    backgroundColor: "#2196F3",
    color: "white",
    "&:hover": {
      backgroundColor: "#1976D2",
    },
  },

  // Complete button styling
  completeButton: {
    backgroundColor: "#FF9800",
    color: "white",
    "&:hover": {
      backgroundColor: "#F57C00",
    },
  },
};

// Animation Configuration
export const ANIMATION_CONFIG = {
  // Slide in animation
  slideIn: {
    from: {
      opacity: 0,
      transform: "translateY(-20px) scale(0.95)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },

  // Slide out animation
  slideOut: {
    from: {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
    to: {
      opacity: 0,
      transform: "translateY(20px) scale(0.95)",
    },
  },

  // Move to ready animation
  moveToReady: {
    "0%": {
      transform: "translateY(0) scale(1)",
      opacity: 1,
    },
    "25%": {
      transform: "translateY(-20px) scale(1.1)",
      opacity: 0.8,
    },
    "50%": {
      transform: "translateY(-40px) scale(1.05)",
      opacity: 0.6,
    },
    "75%": {
      transform: "translateY(-60px) scale(1.02)",
      opacity: 0.4,
    },
    "100%": {
      transform: "translateY(-80px) scale(1)",
      opacity: 0,
    },
  },

  // Move to in progress animation
  moveToInProgress: {
    "0%": {
      transform: "translateY(0) scale(1)",
      opacity: 1,
    },
    "25%": {
      transform: "translateY(20px) scale(1.1)",
      opacity: 0.8,
    },
    "50%": {
      transform: "translateY(40px) scale(1.05)",
      opacity: 0.6,
    },
    "75%": {
      transform: "translateY(60px) scale(1.02)",
      opacity: 0.4,
    },
    "100%": {
      transform: "translateY(80px) scale(1)",
      opacity: 0,
    },
  },

  // Shake animation for ready number bubble
  shake: {
    "0%, 100%": {
      transform: "translateX(0)",
    },
    "10%, 30%, 50%, 70%, 90%": {
      transform: "translateX(-2px)",
    },
    "20%, 40%, 60%, 80%": {
      transform: "translateX(2px)",
    },
  },
};

// Responsive Configuration
export const RESPONSIVE_CONFIG = {
  // Container dimensions for different orientations
  container: {
    portrait: {
      width: "1080px",
      height: "1920px",
    },
    landscape: {
      width: "1920px",
      height: "1080px",
    },
  },

  // Card dimensions
  card: {
    height: "400px",
    margin: {
      portrait: "50px 30px 0 30px",
      landscape: "30px 30px 0 30px",
    },
  },

  // Header styling
  header: {
    portrait: {
      fontSize: "2.5rem",
      marginBottom: "20px",
    },
    landscape: {
      fontSize: "3rem",
      marginBottom: "30px",
    },
  },

  // Number bubble styling
  numberBubble: {
    portrait: {
      width: "60px",
      height: "60px",
      fontSize: "1.5rem",
    },
    landscape: {
      width: "80px",
      height: "80px",
      fontSize: "2rem",
    },
  },

  // Time and date styling
  timeDate: {
    portrait: {
      fontSize: "1.2rem",
      marginTop: "10px",
    },
    landscape: {
      fontSize: "1.5rem",
      marginTop: "15px",
    },
  },
};
